import {Component, EventEmitter, Input, Output, Renderer2, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {DateAdapter} from '@angular/material/core';
import {
    DefaultMatCalendarRangeStrategy,
    MatCalendarView,
    MatDatepicker,
    MatDateRangePicker,
    MatDateRangeSelectionStrategy,
    MAT_DATE_RANGE_SELECTION_STRATEGY,
} from '@angular/material/datepicker';
import dayjs from 'dayjs';
import {
    CALENDAR_HEADER_CONFIG,
    CalendarHeaderConfig,
    CustomCalendarHeaderComponent,
} from './custom-calendar-header/custom-calendar-header.component';
import {StartTrackingRangeStrategy} from './start-tracking-range-strategy';

const DEFAULT_CLASSES = 'mat-calendar-body-cell mat-calendar-body-in-range mat-calendar-body-active';
const START_CLASS = 'mat-calendar-body-range-start';
const END_CLASS = 'mat-calendar-body-range-end';

@Component({
    selector: 'ui-kit-calendar',
    templateUrl: 'calendar.component.html',
    providers: [
        {
            provide: CALENDAR_HEADER_CONFIG,
            useFactory: (component: CalendarComponent) => component.headerConfig,
            deps: [CalendarComponent]
        },
        {
            provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
            useFactory: (component: CalendarComponent, adapter: DateAdapter<Date>) => component.createRangeStrategy(adapter),
            deps: [CalendarComponent, DateAdapter],
        },
    ],
    standalone: false
})
export class CalendarComponent {
    @ViewChild('picker') dateRangePicker: MatDateRangePicker<Date>;
    @ViewChild('datepicker') datePicker: MatDatepicker<Date>;
    @Input() mode: 'date-picker' | 'date-range-picker' = 'date-picker';
    @Input() dateStartControl: FormControl<Date>;
    @Input() dateEndControl: FormControl<Date>;
    @Input() disabled: boolean = false;
    @Input() showPickerActions: boolean;
    @Input() minDate: Date;
    @Input() maxDate: Date;
    @Input() headerConfig: CalendarHeaderConfig = {isCustomViewSwitchingEnabled: false};
    @Input() monthLock = false;
    @Output() closed = new EventEmitter<void>();
    @Output() openClickListener = new EventEmitter<void>();
    CustomCalendarHeaderComponent = CustomCalendarHeaderComponent;

    currentView: MatCalendarView = 'month';
    mutationObserver: MutationObserver;
    lockedAnchor: Date | null = null;

    constructor(private renderer: Renderer2) {
    }

    monthLockFilter = (date: Date | null): boolean => {
        if (!this.lockedAnchor || !date) {
            return true;
        }
        return date.getFullYear() === this.lockedAnchor.getFullYear() && date.getMonth() === this.lockedAnchor.getMonth();
    };

    dateClass = (date: Date, view: MatCalendarView): string => {
        const start = this.dateStartControl.value;
        const end = this.dateEndControl.value;

        if (!start || !end) {
            return '';
        }

        const year = date.getFullYear();
        const month = date.getMonth();

        const startDate = new Date(start);
        const endDate = new Date(end);
        const startYear = startDate.getFullYear();
        const startMonth = startDate.getMonth();
        const endYear = endDate.getFullYear();
        const endMonth = endDate.getMonth();

        if (startDate.getTime() === endDate.getTime() || startMonth === endMonth || startYear === endYear) {
            return '';
        }

        if (view === 'multi-year') {
            if (year === startYear) {
                return `${DEFAULT_CLASSES} ${START_CLASS}`;
            } else if (year === endYear) {
                return `${DEFAULT_CLASSES} ${END_CLASS}`;
            } else if (year > startYear && year < endYear) {
                return DEFAULT_CLASSES;
            }
        } else if (view === 'year') {
            if (year === startYear && month === startMonth) {
                return `${DEFAULT_CLASSES} ${START_CLASS}`;
            } else if (year === endYear && month === endMonth) {
                return `${DEFAULT_CLASSES} ${END_CLASS}`;
            } else if ((year === startYear && month > startMonth)
                    || (year > startYear && year < endYear)
                    || (year === endYear && month < endMonth)
            ) {
                return DEFAULT_CLASSES;
            }
        }
        return 'mat-calendar-body-cell';
    };

    currentViewChanged(event: MatCalendarView): void {
        this.mutationObserver?.disconnect();
        this.dateRangePicker?._applyPendingSelection();
        this.currentView = event;
        if(this.dateRangePicker){
            this.subscribeToOverlayChanges();
        }
    }

    _applyPendingSelection(): void {
        if (this.datePicker) {
            this.datePicker._applyPendingSelection();
            return;
        }
        this.dateRangePicker?._applyPendingSelection();
    }

    open(): void {
        if (this.datePicker) {
            this.datePicker.open();
            return;
        }

        this.dateRangePicker?.open();
        this.dateRangePicker?._applyPendingSelection();
        this.applyMonthLock(null);
        this.mutationObserver?.disconnect();
        this.subscribeToOverlayChanges();
    }

    close(): void {
        this.datePicker?.close();
        this.mutationObserver?.disconnect();
        this.dateRangePicker?.close();
        this.applyMonthLock(null);
    }

    createRangeStrategy(adapter: DateAdapter<Date>): MatDateRangeSelectionStrategy<Date> {
        if (!this.monthLock) {
            return new DefaultMatCalendarRangeStrategy(adapter);
        }
        const strategy = new StartTrackingRangeStrategy(adapter);
        strategy.onStartChange = start => this.applyMonthLock(start);
        return strategy;
    }

    applyMonthLock(start: Date | null): void {
        this.lockedAnchor = start;
    }

    private subscribeToOverlayChanges(): void {
        const overlayContainer = document.querySelector('.cdk-overlay-container');
        this.mutationObserver = new MutationObserver(() => {
            const cells = overlayContainer?.querySelectorAll('.mat-calendar-body-cell');

            if (cells) {
                cells.forEach((cell, index) => {
                    this.renderer.listen(cell, 'mouseover', () => {
                        if (this.dateStartControl.value && !this.dateEndControl.value && this.currentView !== 'month') {
                            this.clearAllHighlights(cells);
                            this.highlightCellsUpTo(cells, index, this.dateStartControl.value);
                        }
                    });

                    this.renderer.listen(cell, 'mouseleave', () => {
                        if (this.dateStartControl.value && !this.dateEndControl.value && this.currentView !== 'month') {
                            this.clearAllHighlights(cells);
                        }
                    });
                });
            }
        });

        if (overlayContainer) {
            this.mutationObserver.observe(overlayContainer, {childList: true, subtree: true});
        }
    }

    private highlightCellsUpTo(cells: NodeListOf<Element>, hoveredCellIndex: number, date: Date): void {
        for (let i = 0; i <= hoveredCellIndex; i++) {
            const startYear = date.getFullYear();
            const startMonth = date.getMonth();
            const cellToHighlightValue = cells[i].getAttribute('aria-label');
            if (this.currentView === 'multi-year') {
                if (+cellToHighlightValue > startYear) {
                    this.renderer.addClass(cells[i], 'mat-calendar-body-in-preview');
                    if (i === hoveredCellIndex) {
                        this.renderer.addClass(cells[i], 'mat-calendar-body-preview-end');
                    }
                }
                if (+cellToHighlightValue === startYear) {
                    this.renderer.addClass(cells[i], 'mat-calendar-body-in-preview');
                    this.renderer.addClass(cells[i], 'mat-calendar-body-preview-start');
                }
                if (+cellToHighlightValue > startYear && i === hoveredCellIndex) {
                    this.renderer.addClass(cells[i], 'mat-calendar-body-preview-end');
                }
            } else if (this.currentView === 'year') {
                let cellDate = dayjs(cellToHighlightValue.trim(), 'D MMMM YYYY г.', 'ru');
                if (cellDate.isValid()) {
                    return;
                }
                cellDate = dayjs(`1 ${cellToHighlightValue.trim()}`, 'D MMMM YYYY г.', 'ru');
                if (startYear >= cellDate.year() && cellDate.month() > startMonth || cellDate.year() > startYear) {
                    this.renderer.addClass(cells[i], 'mat-calendar-body-in-preview');
                    if (i === hoveredCellIndex) {
                        this.renderer.addClass(cells[i], 'mat-calendar-body-preview-end');
                    }
                }
                if (cellDate.year() === startYear && cellDate.month() === startMonth) {
                    this.renderer.addClass(cells[i], 'mat-calendar-body-in-preview');
                    this.renderer.addClass(cells[i], 'mat-calendar-body-preview-start');
                }
            }
        }
    }

    private clearAllHighlights(cells: NodeListOf<Element>): void {
        cells.forEach((cell) => {
            this.renderer.removeClass(cell, 'mat-calendar-body-in-preview');
            this.renderer.removeClass(cell, 'mat-calendar-body-preview-start');
            this.renderer.removeClass(cell, 'mat-calendar-body-preview-end');
        });
    }
}
